import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { CardBody } from 'reactstrap';

import classNames from 'classnames';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldType from '../../../../types/FormFieldType';
import CheckoutServiceMethodList from '../../common/CheckoutServiceMethodList';
import ApplePaySupport from '../ApplePaySupport';
import CheckoutPaymentMethodContent from '../CheckoutPaymentMethodContent';

const CheckoutPaymentMethod = ({ onOrderUpdate, checkoutStore }) => {
  const { order } = checkoutStore;
  const {
    paymentMethodGroups,
    selected_payment,
    shouldDelayVerifonePaymentMethods,
  } = order;

  const form = useMemo(() => {
    const groupType = selected_payment?.group?.type;
    const methodId = selected_payment?.method?.id;

    return createForm({
      paymentMethodGroupList: createFormField({
        type: FormFieldType.BOOLEAN,
        value: groupType !== 'CUSTOM' ? groupType : null,
        required: false,
      }),
      paymentMethodCustomGroupList: createFormField({
        id: 'CUSTOM',
        type: FormFieldType.BOOLEAN,
        value: groupType === 'CUSTOM' ? methodId : null,
        required: false,
      }),
    });
  }, [selected_payment?.group?.type, selected_payment?.method?.id]);

  const renderMethodGroupContent = (option) => {
    const group = order.selected_payment?.group;

    if (!group) {
      return null;
    }

    if (group.type === option.value) {
      return <CheckoutPaymentMethodContent onOrderUpdate={onOrderUpdate} />;
    }

    return null;
  };

  const renderMethodContent = (option) => {
    const method = order.selected_payment?.method;

    if (!method) {
      return null;
    }

    if (method.id === option.id && option.description) {
      return (
        <CheckoutPaymentMethodContent
          content={
            <div className="CheckoutPaymentMethod__option-description">
              <p dangerouslySetInnerHTML={{ __html: option.description }} />
            </div>
          }
        />
      );
    }

    return null;
  };

  const updatePaymentGroupPaymentMethod = (option) => {
    order.updateSelectedPayment({ paymentMethodGroupId: option.value });
    onOrderUpdate({ withoutTimer: true });
  };

  const updatePaymentCustomGroupPaymentMethod = (option) => {
    // Set scroll ID for custom payment methods (group type is 'CUSTOM')
    order.setScrollToMethodId(
      `CheckoutPaymentMethodContent-CUSTOM`.toLowerCase()
    );
    order.updateSelectedPayment({
      paymentMethodGroupId: option.id,
      paymentMethodId: option.value,
    });
    onOrderUpdate({ withoutTimer: true });
  };

  return (
    <CardBody
      className={classNames(
        'CheckoutPaymentMethod',
        'CheckoutPaymentMethod__content'
      )}
    >
      <ApplePaySupport />
      {!shouldDelayVerifonePaymentMethods && (
        <>
          <CheckoutServiceMethodList
            className="CheckoutPaymentMethod__payment-methods"
            onMethodUpdate={updatePaymentGroupPaymentMethod}
            form={form}
            fieldName={'paymentMethod'}
            field={'paymentMethodGroupList'}
            options={paymentMethodGroups.groups}
            content={renderMethodGroupContent}
            methodImageVariant="checkout-method-list"
          />
          <CheckoutServiceMethodList
            className="CheckoutPaymentMethod__custom-payment-methods"
            onMethodUpdate={updatePaymentCustomGroupPaymentMethod}
            form={form}
            fieldName={'paymentMethod'}
            field={'paymentMethodCustomGroupList'}
            options={paymentMethodGroups.customGroups}
            optionContent={renderMethodContent}
            methodImageVariant="checkout-method-list"
          />
        </>
      )}
    </CardBody>
  );
};

CheckoutPaymentMethod.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
};

export default inject('checkoutStore')(observer(CheckoutPaymentMethod));
